import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatOptionModule } from '@angular/material/core';
import { MatIconModule } from '@angular/material/icon';
import { MatDialogModule } from '@angular/material/dialog';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { URLBuilder } from 'src/app/core/utils/url-builder';
import { MatSnackBar } from '@angular/material/snack-bar';
import { HttpErrorResponse } from '@angular/common/http';

interface ProcessType {
  id: number;
  nome: string;
}

interface AuthResponse {
  listaObj: { token: string }[];
}

interface ProcessTypesResponse {
  listaObj: ProcessType[];
}
@Component({
  selector: 'app-process-type-dialog',
  standalone: true,
  templateUrl: './process-type-dialog.component.html',
  imports: [
    CommonModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    ReactiveFormsModule,
    MatProgressSpinnerModule,
    MatSelectModule,
    MatOptionModule,
    MatIconModule,
    MatDialogModule
  ],
})
export class ProcessTypeDialogComponent implements OnInit {
  processTypeForm!: FormGroup; // Formulário reativo
  processTypes: any[] = []; // Tipos de processo
  isLoading = false; // Estado de carregamento
  private urlBuilder = new URLBuilder();


  constructor(
    private readonly fb: FormBuilder,
    private readonly dialogRef: MatDialogRef<ProcessTypeDialogComponent>,
    private readonly http: HttpClient,
    private readonly snackBar: MatSnackBar
  ) {}

  ngOnInit(): void {
    this.initializeForm();
    this.loadProcessTypes();
  }

  private initializeForm(): void {
    this.processTypeForm = this.fb.group({
      processType: ['', Validators.required],
      dueDays: [1, [Validators.required, Validators.min(1)]],
    });
  }

  // Process Types
  private async loadProcessTypes(): Promise<void> {
    this.isLoading = true;
    const token = await this.getToken();

    if (!token) {
      this.processTypes = this.getCachedProcessTypes();
      this.isLoading = false;
      return;
    }

    try {
      const url = this.urlBuilder.buildEndpoint('service/processo/tipos/abertura');
      const response = await firstValueFrom(
        this.http.get<ProcessTypesResponse>(url, { headers: this.getHeaders(token) })
      );

      this.processTypes = response?.listaObj || [];
      localStorage.setItem('processTypes', JSON.stringify(this.processTypes));
    } catch (err) {
      console.error('Error loading process types:', err);
    } finally {
      this.isLoading = false;
    }
  }

  private async getToken(): Promise<string | null> {
    const token = await this.authenticate();
    if (!token) return null;
    localStorage.setItem('authToken', token);
    return token;
  }

  private async authenticate(): Promise<string | null> {
    const loginData = { login: '33870041000198', senha: 'HgtUop@8' };
    const url = this.urlBuilder.buildEndpoint('service/autenticacao/login');
  
    console.log('Starting authentication...');
    console.log('Login Data:', loginData);
    console.log('Authentication URL:', url);
  
    try {
      const response = await firstValueFrom(
        this.http.post<AuthResponse>(url, loginData, {
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' }
        })
      );
  
      console.log('Authentication response:', response);
  
      // Check if the response contains the token
      const token = response?.listaObj?.[0]?.token;
      if (token) {
        console.log('Token received:', token);
        localStorage.setItem('authToken', token);
        return token;
      } else {
        console.error('Token not found in response');
        return null;
      }
    } catch (err: unknown) {
      console.error('Authentication failed:', err);
  
      // Check if error has a status code (HTTPErrorResponse)
      if ((err as HttpErrorResponse).status === 400) {
        this.notifyUser('Requisição inválida. Verifique os dados de login.');
      } else if ((err as HttpErrorResponse).status === 401) {
        this.notifyUser('Não autorizado. Verifique suas credenciais.');
      } else if ((err as HttpErrorResponse).status === 500) {
        this.notifyUser('Erro interno do servidor. Tente novamente mais tarde.');
      } else {
        this.notifyUser('Erro desconhecido ao autenticar.');
      }
      return null;
    }
  }

  private getHeaders(token: string) {
    return {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      Accept: 'application/json',
    };
  }

  private getCachedProcessTypes(): ProcessType[] {
    const storedValue = localStorage.getItem('processTypes');
    return storedValue ? JSON.parse(storedValue) : [];
  }

  onSubmit(): void {
    if (this.processTypeForm.invalid) {
      this.processTypeForm.markAllAsTouched();
      return;
    }

    const { processType, dueDays } = this.processTypeForm.value;
    const selectedProcessType = this.processTypes.find(
      ({ id }) => id === processType
    );

    if (selectedProcessType) {
      this.dialogRef.close({
        processTypeName: selectedProcessType.nome,
        processTypeId: selectedProcessType.id,
        dueDays,
      });
    }
  }

  hasError(controlName: string, errorName: string): boolean {
    return this.processTypeForm.get(controlName)?.hasError(errorName) ?? false;
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  private notifyUser(message: string): void {
    this.snackBar.open(message, 'Fechar', {
      duration: 3000, 
      panelClass: ['snackbar-error'] 
    });
  }
}
